import {
  assertEvent,
  assign,
  fromPromise,
  sendTo,
  setup,
  type SnapshotFrom,
} from 'xstate';
import type {IEvent} from '../../types/IEvent';
import {bookingMachine} from '../../machines/bookingMachine';
import {identificationMachine} from '../../machines/identificationMachine';
import {rsvpMachine} from '../../machines/rsvpMachine';
import {
  cancelEvent,
  getEvent,
  getEventTemplate,
  getReasons,
  getSession,
  rescheduleRequest,
} from '../../services/schedularService';
import type {IParticipantType} from '../../types/IParticipantType';
import {getShortSha256Hash} from '../../utils/crypto';
import {isTimezoneSupported} from '../../utils/booking';

import type {
  IRootMachineContext,
  IRootMachineEvents,
  IRootMachineInput,
} from './rootMachine.types';
import {getRsvpStatus} from '../../utils/common';
import {isBrowserSupported} from '../../utils/browser';


export const rootMachine = setup({
  types: {
    context: {} as IRootMachineContext,
    events: {} as IRootMachineEvents,
    input: {} as IRootMachineInput,
  },
  actors: {
    getEvent: fromPromise(async ({input}: {input: {eventId: string}}) =>
      getEvent(input.eventId),
    ),
    getSession: fromPromise(async ({input}: {input: {sessionId: string}}) =>
      getSession(input.sessionId),
    ),
    getTemplateBySuffix: fromPromise(
      async ({input}: {input: {agentId: string; suffix: string}}) =>
        getEventTemplate(input.agentId, input.suffix),
    ),
    getReasons: fromPromise(async () => getReasons()),
    getOwner: fromPromise(
      async ({
        input,
      }: {
        input: {eventId: string; attendee?: string};
      }): Promise<IParticipantType | undefined> => {
        const guestHash = await getShortSha256Hash(`guest_${input.eventId}`);
        if (guestHash === input.attendee) {
          return 'CANDIDATE';
        }
        const hostHash = await getShortSha256Hash(`host_${input.eventId}`);
        if (hostHash === input.attendee) {
          return 'HOST';
        }
        const interviewerHash = await getShortSha256Hash(
          `interviewer_${input.eventId}`,
        );
        if (interviewerHash === input.attendee) {
          return 'INTERVIEWER';
        }
        return undefined;
      },
    ),
    cancelEvent: fromPromise(
      async ({
        input,
      }: {
        input: {
          eventId: string;
          reason: string;
          viewerTimezone: string;
          attendee: string;
          aid: string;
        };
      }) =>
        cancelEvent(input.eventId, {
          viewer_timezone: input.viewerTimezone,
          attendee: input.attendee,
          reason: input.reason,
          aid: input.aid,
        }),
    ),
    sendRescheduleRequest: fromPromise(
      async ({
        input,
      }: {
        input: {eventId: string; reason: string; aid: string};
      }) =>
        rescheduleRequest(input.eventId, {
          reason: input.reason,
          aid: input.aid,
        }),
    ),
    generateAttendee: fromPromise(async ({input}: {input: {eventId: string}}) =>
      getShortSha256Hash(`guest_${input.eventId}`),
    ),
    rsvpMachine,
    bookingMachine,
    identificationMachine,
  },
  guards: {
    isBrowserNotSupported: () => !isBrowserSupported(),
    isRescheduleLimitReached: ({context}) => {
      return (context.eventDetails as IEvent)?.reschedule_attempts_left === 0;
    },
    isEditBooking: ({context}) => {
      if (!context.currentRoute) {
        return false;
      }
      return ['cancel', 'reschedule', 'rsvp'].includes(context.currentRoute);
    },
    isSession: ({context}) => !!context.sessionId,
    isCancellation: ({context}) => context.currentRoute === 'cancel',
    isReschedule: ({context}) => context.currentRoute === 'reschedule',
    isRsvp: ({context}) => context.currentRoute === 'rsvp',
    isCancellationNoticePass: ({context}) =>
      !!(context.eventDetails as IEvent)?.did_cancellation_notice_pass,
    isAttendeeAvailable: ({context}) => !!context.attendee,
    isHostOrInterviewer: ({context}) =>
      context.owner === 'HOST' || context.owner === 'INTERVIEWER',
    isCandidate: ({context}) => context.owner === 'CANDIDATE',
    isSessionExpired: ({context}) => !!context?.session?.is_expired,
    isTimezoneSupported: ({context}) =>
      context.timezone ? isTimezoneSupported(context.timezone) : false,
    isBookingSuccess({event}) {
      assertEvent(event, 'xstate.done.actor.bookingFlow');
      return event.output.finalState === 'BookingSuccess';
    },
    isSequentialEvent: ({context}) => {
      // Check if eventDetails is an array  for booking sequence OR event has group_id for any edit booking
      return (
        Array.isArray(context.eventDetails as IEvent[]) ||
        Boolean((context.eventDetails as IEvent)?.group_id)
      );
    },
  },
}).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QCcD2qAuBiANgQwDsoBXPGAOmIAcI8MwBtABgF1FQrVYBLDb1AuxAAPRAEYAHADYAzOSYBOAExMALBLEB2KWMkKANCACeiALSbNE+duVKlszQFYmEgL6vDaTOVgY8ybGY2JBBOHj4BIVEEUzEZdXIZBRlHCxkpKQks1UMTGMcZOXUlVQySnSltd090DB8-AKwGMWCOLl5+QRDo0wlHJXJHZyYZCUVpKQUJXLMCoolyqXKxSqlqkC86339ApVbQ9oiu0GiV8lUlZQ0rqbixGfylKwvNC4VNJk-Stw8N2vI+ABbMAALwEYAAMqg8BBIE1WEIwh1It0zNpnk5rjIVBIFP0HqYUkx5GIFKoZDYxKpkqp1ptyMgwHhYAJYFCYdwiFgIODyJyAG6oADWYAZ-0ZzNZ7IgnKgCAFqAAxnROkEgojDp0omYJJoFPIRitdZZJqocsYzBlEpctBSCkwhrq6eKmSyCGzoTKuTyCKKFSKxd4JW6PRyiPKCILlUc1S0NeEtaiYmIlHIRpMli4pFmnASnORdPZNGImCX+goxM6g66pZ7Zdzef7RfTg7Ww3KFdHVawGHt48jjiJEO9VORJja7CUJKoCgTdYlsSXVJIZJ9Rmtfi2a+7pfWwMg0MhyFR8BgAGaoZCAwN1Vs7uvhzsqgRqhEhJFHbUxUrExy61MVmoTA6EomgEio+pMBYxqvKky7AVWt7brAACiB6XlgjIYMgRjqu+mooicsx-uQqZGs4SiOBWyTgZU5CWJ8Yh-uopR6ohALcMCYK+gACtwioisgWBAqCvKwGAGB4W0CaEUOMR2mOpQFNmTCphc9gEqoHyDHqCxPBI2JPDI7FgPyYAEBgu7eo2kbCs2-ymeZlkPh2tldi+PZvtJA5fr0IykdmZLQUkuIGBaCCpo45AKJMQyOLoQzxAoJlmRZVlQFg+6Hsep4XleN7kI5aUuRGUbPgQr77B+iZEd+8XkNI07kpMKbKPc4V3KRWnJPYsjxLISjsYqAAWYACQAymAACOxBOdweA4ChqWBF5BwyYOPQLKOuIUlpxZUfpDzUsSmTxLiLjpKpJRDaNE3TbNFnzYty1NHG+Hrb5-QDLiJTUrqq66EdCgnQZ6jAwZ2apux4mwDwAjpQ2vp8rZAb0jDcMEOlpVKuVlX9p+SbOGIBYfKuwNTKWuKOA8FJWHYaglq8ihPNDcAYwjWWXjldB5deaNs50WNPjGnlVQRG3iKp+qFNiGRUSmubhSoUXYkkqSaDa1KOClTkACpgICJ50JCLmI36KP2d4RUYPrhunib7bY+5FWi-jNVySsZKDOkJaqcuWmpA8BkDA6yQ2BrqRSNrm4OctttG-QHPoUeCe8wV1vx-bQtubjrvvT5SZKExUiJFSlFmikqiKFIDxR6OChtZOerLn07GwvQV6cmAADyADuvpCT65uCqj-zt-ugJd33A9O7nLBSWtBe1aYBQDMktjZnamS11kgwZFp6gOLYOvFe2aGHphEk4Qv1WyT0jqkTIKzpsdfTtXkGsk6Bum4uoT+aKzWGgtPRwhvuLL86grCVClvYeCoEwp5EqFYYs0h4qgSkBXYyMdvDo2ATCUBb1vIE1qlSeIiRSyUUag6CkO80yKH6qSRwWktCAIxufDCWFr6rVvhLBABkooWEUEMFQ6Q2oPDLgWSiodqSSCLslbBdQABG6AhSygAGI4FQL3M2yMR6imUcKdRmje5gI+kmFYCQG6rmpKI5cZJxEpmJlpcYegMi6lpAo8gBjVFEA0VonRTYvEqKMVo5oYszG1SLhgxSGRPiWCSHic0eRdAUkGFkd4UdSwFErJ47xITtH4CIKQCg1BaD0FMUvOSRdtKZFkDOJIp1ZzhWnAMUkM5SzkirksABniM4GwTmAdhQlOG4W4eAwuIw0zDCYevEo2IHhMOJjiQomQSzpH6OxLRA9pSgLGREuSK8njRXHIxJi6h4riLUFYP8VzWIpA+D0mo3gtn7h2RAV64TKnRH6qRPoVcFgOiYaBamHUrlpNuZUe5UFNn91eSA95vZPnEI9kkEOMV-x4iWDOEFySwU3JYpC1I0LcnBKIONYgipFRs3hEi92pxiz6i0E8ckrwG5MOmB1D4o5HCyAwRrDWiSsFPKUaSqA5LKXUrCW7O+iBlb6gjtodQpJpBJPEMWAYM5tCFDiUI+RwryAwAHsbAAghgegBBYRgACRbAqhr9wmrNeZS1s8Rbzz2V8tVVoKQOmAvy4sSxxF4mJqkWwUFUgqA8fq5UBAqU4FPJ0Pxvdka8BpdK3hsQtb0WBZIck6SZziIBvIbMxQAIZBSENQgsb40CETcmwIhDF7Ip6I40c-ULBmkyFBJiBbSxFquXYBpsho5RsrWAON5Va2chTYitNvkxgDCJACyiGtxg9uJsBftpah0VpjWO6tBBa13jUZea8vNL6SgPSenwxBFGT0ku6ptZhdDzi+nqCwmIqQ4vEH+KwF14nJC0C4Qanjo1VoncYhk25j35VgDeu9fBrJI0CfSUDe7wNaMgxe6D15YO3t4Ah1yZVXUVMfcmSQxJlCKFJh2quqqECMJLt6xQmLXiZEeX8bwqHx1HEPVBq9uH4N7mTtzc8V6UOju4wmiDR7+Nwfw7KF13Y3W0plTEPUJcUyKGXJXZ+79xA6EgpceK2I-xGaFRxuoXH928aw1ezmwy+Mwbk-elT6bkhFH0vpc5UcwIdSGMGtWepCjUXSOxRksARqQGIDgMAtacCcV4AAOUwAAJSZJFhFD66WysovRLplG1bl188k9SpFKKr0anELIYW4AZei7FiD8W73JYwGlvAGWPmzsJjy+iPLlyr0mLYAtDcuq4gyBg9I2J2Nbgi6NCA9W4sJYwC1trHWZ351I2MYmDcsiXAwSWFYShxGlbsIlJ4qzRg-H1eFurMWbNumw+QM9MmnN4Zc115eVF6LxBWM+7McEUjiPnQuOI77kjGY3Nd2rc2FvScczh5zBHrV6IKjdmHd24e2de4Jx8OdiNZdU08fUVdSSUUkM4UzgaZxjim7IPETCq7Aah7NqLGOMMvYR29pH9mRNpxm7dhr7P4fXq5wp4WSmSPZYQCG77uIxi2MojIIHFwGrLksLR7zw6LOQZZ-NtnSaOeFWTueh7sm3uS9U9p+Qy5SQVlMyobQ4jNBkIVh8aprwDI5OZwL2teTfHGOR3ZIJhj-ehIJ7w0H+o0FmjxMBd4Fy-O6ESEzVFVcYqpBq7r2HGG-dQETYHgMufE1So21L0kT9yGUjsFtaQ4j+j1wuOSUsepSde+12j1nguk1F4D4UkgZBRSlONhbiPeLV6WHj52jlyT5bnCWEMCwPKqQpkzz7iDU6Vquc+gIxQnsKyNQjuIy4chUy-WAv8vo02XRZ-13WzrpfCeXG+4lR0-XZFH5G+DpwK7KJGXYrngAYQk2wywEAOALNzvRHy-CSGJGXyonU1AifgQVlS0CsF0BpAbksGdyv28DAN3RwHJVF0Q2HiD3pDwNjUIJx0Ixxnxy3yTF6GBkSAwWyS1hlmKxy1UkGDxA1g0BESqBJRDygCAPwMoPky5B51TjE3+HILHVELNTFzxwl3DznWJyrhDXV2cHiCVyVmYzHBcGyApCcArH-1FWENjSGVANMPAOx14CgPoJnGJghhbh+iLhTAeAjTkGnCWEyB-FSFGHYjoHNUtQAHFzJ7V6B3kO89dRQoBUA7CSF1BiRnc2kLh+EUwa4Op+FopgY3cMkbQAjHULUwAwBQijUIisArNyoDU4jlDzE9oSQn5L8HQNBpx3CVB11uCzREDP0NxfgCBUBYR4AQhNgPsDlPZiRRgREmJds-wv0YhMxooFgmERh-VGdoYGgMBRjm0qIS4YpVIHQmJVJ4h2D8hVwxx207AUx-5Sx2JiB3RqBOAAhIAAAhNAXucSZALYp9LQkmPoeKbQD4KYE40wEnUiYGIYcmdIBiSNbXESbiB2WECAL4mIDQYmJcZ3XUPoBmYEs0bbHwy4iYffGrC9UML0KAZE3oUkc4UmSYVjacVo8KUwMnAsY6QRUoVcKOSHdvZCIZCkjtQYXfJ4OIEoEsEoWiTQeiMYLJek1iPVWEziUSXifiQSZElMItCCSmGRFwTSLSPQ2Wbw53BuKkE+ZydsCkjQEuCwHzOIOxPEIONAwCKYKickGKY0kDW6IUSaGaOaBaJaJyc0ivKOMkJ4HlEzJIGmV4XrDBKuRcH9GE-mIBeGFyZEpVL+XwmZJ4R3TIooX7BKQoACeM2OPWfpLOZMh-CPFqeQbEC6NQ8nZAhAMoVXRQB0bQRiewNuCSCeKeWFT48sz6KuAsCiINW0WvcKSOc4VSCbG0smE09KXkvs+wpIMcDQGcIuP8DMQHcKT+JldxIYTJKuHArYAWJM-BJEhc2qakRlbMZInQLQLMvIf4+iC6ACUYJ+YlfVXBAQecohMvEYAYTIG0HlVjKcY7VpKRZ0-fIuacEwwQxNVUyoVpeKMkKCB5ewI7cKMkaWfoHQDQUzM0LXekPpO2Y2b8xtKXapOhI0OpCsDIBZUoLNQwlIFchJGFbZeFCk+KX9FVIuVFN+bQvIaQbbXEJwSKevUoVmb0x6X05aFrY1ONLRSAc0yYAsFMP8LVSjBkh86QPeNQf2bMFYAi-4T8ggFCYQKgbgRkM8n8y3ecIsJ0llQyRWD+PoEHX6OYN9OUsg0VcVKlWGVU53OQevS4WPAyP2cRfSg0CONPKCIM9iO1ZAB1II4o5ElQOiZ9J+TpIDafcQZIEuDtW88HUoLk-mSSvgBaZ47yilXyoY6yiPD4OmckWnKCCkSQJ3EbP4lIbEFVRmdifovgM8IwHytmFKlIddLqhwpmZwDImfOwAsZQUYUhKU3EHdMDHjYxCkixBdAFJ+H9ShOY9AtA2QP5ZcCoJnbXSotajDDfDa50xIba6YpYvoD-KwDQQFa8g+FatDS6pNC6zoHiVAeLRUPIWq3yT9a5CbAK23UYY7bEGnCwcvewYOT6yTGtTHU3K8DayKQYOwN9RfFc-a8kDVCiOIGKg+QyzjCTazNGmtK9XmDa37BqMYFifCy4UcmfKk2PfoekjIO85GqmoXLHTnKg+mr1AaVI5QWJfa6MnSMuG5BYK7c6ym9DA3YXezCkyoKKea3gtcuwUoOveqAobg9zELYq-4X61Gq62ER6M8fiZWjivEX5Qocje5GcOjFMFystCsbVCsFlVfdHLvCkqcMcKcKRAGGi6a8QNpBqflL26kCoQs6sG-LvcgJrJLVLdLObFKouRISCrmp0xXAtL7AGUYVSMuKcP2zve7GmjG88g5FQE6EOzQ0scO8K+i7peJV84CeICu6Iquy9fKOm2u7Y-8xukYZulYCO+jJYwYENQoNIKYKYHu7PFWwWkXYWoe-TYkC4SoX7dy6xIHKCaKGxZuv5UCJe2-Q3NWjehspYbG+dAyCkOndC5JJmOfb1EoZIG4zxKI5e4PHxPPda6+pYDVYCewTFIC1SOvSQc4VMXSKYF4Re7+6HSu9fK2-q22761UtQE-UO+dWQB0Y7RQBqRG8mQ+BYU2xOtfK6ggXgDa53FStSdA2RedI-BYZPN8jW+PRB73f23jJOiEJbVbTO6+kEqKKWCiB5LQ7UjqEu+iDBbICNaQTy8UWAfkKgOC6+qkQ+zC53XWhuKCN2qCveLpVBXSbugQ-+swsdbDLBh0GepIdtYsLA+s+mDouCF4DKihkVQQqxggxHWUdW+qf+eR0CYK9Qdwu8rgnlZujBPEaCkDCTIavykR59CUskQSu4P8MkPTCKW3VXSOFIYKlwGCyxiTUinhUGlQNJdVSYu3Z3OYjw1Xbwrwls0LTxNLb03wNLJOpJmqsi1Tbg0iUUrAs5QoQNFIMcVIPQbe5IZazxQIp1Yo0o8IxSzR9kscWKT4fec5E40kT+MYYtD+4GOIdwdwIAA */
  context: ({input}) => ({
    id: input.id,
    currentRoute: input.currentRoute,
    attendee: input.attendee,
    aid: input.aid,
    timezone: input.timezone,
    sessionId: input.sessionId,
    language: input.language,
    countyCode: input.countyCode,
  }),
  id: 'root',
  initial: 'start',
  on: {
    'language.update': {
      actions: assign({
        language: ({event}) => event.language,
      }),
    },
  },
  states: {
    start: {
      always: [
        {
          target: 'unsupportedBrowser',
          guard: {
            type: 'isBrowserNotSupported',
          },
        },
        {
          target: 'timezoneLoaded',
          guard: {
            type: 'isTimezoneSupported',
          },
        },
        {
          target: 'timezonePicker',
        },
      ],
    },

    unsupportedBrowser: {
      type: 'final',
    },

    timezoneLoaded: {
      always: [
        {
          target: 'reasonsLoading',
        },
      ],
    },

    reasonsLoading: {
      invoke: {
        id: 'rootNew.reasonsLoading:invocation[0]',
        input: () => ({}),
        onDone: [
          {
            target: 'eventLoading',
            guard: {
              type: 'isEditBooking',
            },
            actions: assign({
              reasons: ({event}) => event.output,
            }),
          },
          {
            target: 'sessionLoading',
            guard: {
              type: 'isSession',
            },
            actions: assign({
              reasons: ({event}) => event.output,
            }),
          },
          {
            target: 'eventTemplateLoading',
            actions: assign({
              reasons: ({event}) => event.output,
            }),
          },
        ],
        onError: {
          target: 'reasonsError',
          actions: assign({error: ({event}) => event.error}),
        },
        src: 'getReasons',
      },
    },

    reasonsError: {
      on: {
        retry: {
          target: 'reasonsLoading',
        },
      },
    },

    timezonePicker: {
      on: {
        'timezone.set': {
          target: 'timezoneLoaded',
          actions: assign({
            timezone: ({event}) => event.timezone,
          }),
        },
      },
    },

    eventLoading: {
      invoke: {
        id: 'rootNew.eventLoading:invocation[0]',
        input: ({context: {id}}) => ({eventId: id}),
        onDone: {
          target: 'checkSequentialEvent',
          actions: assign({
            eventDetails: ({event}) => event.output,
          }),
        },
        onError: {
          target: 'eventLoadingError',
          actions: assign({error: ({event}) => event.error}),
        },
        src: 'getEvent',
      },
    },

    checkSequentialEvent: {
      always: [
        {
          target: 'sequentialEventEditNotAllowed',
          guard: {
            type: 'isSequentialEvent',
          },
        },
        {
          target: 'determineOwner',
        },
      ],
    },

    sessionLoading: {
      invoke: {
        input: ({context}) => {
          if (context.sessionId) {
            return {
              sessionId: context.sessionId,
            };
          }
          throw new Error('sessionId not found');
        },
        onDone: {
          target: 'sessionLoaded',
          actions: assign({
            session: ({event}) => event.output,
          }),
        },
        onError: {
          target: 'sessionError',
          actions: assign({error: ({event}) => event.error}),
        },
        src: 'getSession',
      },
    },

    eventTemplateLoading: {
      invoke: {
        input: ({context: {currentRoute, id}}) => ({
          agentId: currentRoute,
          suffix: id,
        }),
        onDone: {
          target: 'bookingFlow',
          actions: assign({
            eventTemplate: ({event}) => event.output,
          }),
        },
        onError: {
          target: 'eventTemplateError',
          actions: assign({error: ({event}) => event.error}),
        },
        src: 'getTemplateBySuffix',
      },
    },

    determineOwner: {
      invoke: {
        id: 'rootNew.determineOwner:invocation[0]',
        input: ({context}) => {
          if ((context.eventDetails as IEvent)?.id) {
            return {
              eventId: (context.eventDetails as IEvent)?.id,
              attendee: context?.attendee,
            };
          }
          throw new Error('eventId not found');
        },
        onDone: {
          target: 'ownerLoaded',
          actions: assign({
            owner: ({event}) => event.output,
          }),
        },
        src: 'getOwner',
      },
    },

    eventLoadingError: {
      on: {
        retry: {
          target: 'eventLoading',
        },
      },
    },

    sessionLoaded: {
      always: [
        {
          target: 'sessionExpired',
          guard: {
            type: 'isSessionExpired',
          },
        },
        {
          target: 'eventTemplateLoading',
        },
      ],
      entry: assign({
        language: ({context}) =>
          context.session?.guest_language || context.language,
      }),
    },

    sessionError: {
      on: {
        retry: {
          target: 'sessionLoading',
        },
      },
    },

    bookingFlow: {
      on: {
        'language.update': {
          actions: [
            assign({
              language: ({event}) => event.language,
            }),
            sendTo(
              ({system}) => system.get('bookingMachine')!,
              ({event}) => ({
                type: 'language.update',
                language: event.language,
              }),
            ),
          ],
        },
      },
      invoke: {
        id: 'bookingFlow',
        input: ({context: {timezone, eventTemplate, language, session}}) => {
          if (timezone) {
            return {
              timezone,
              eventTemplate,
              language,
              session,
            };
          }
          throw new Error('timezone not found');
        },
        src: 'bookingMachine',
        systemId: 'bookingMachine',
        onDone: [
          {
            target: 'bookingSuccess',
            actions: assign({
              eventDetails: ({event}) => event.output.eventDetails,
            }),
            guard: {
              type: 'isBookingSuccess',
            },
          },
          {
            target: 'notifySuccess',
          },
        ],
      },
    },

    eventTemplateError: {
      on: {
        retry: {
          target: 'eventTemplateLoading',
        },
      },
    },

    ownerLoaded: {
      always: [
        {
          target: 'cancellationFlow',
          guard: {
            type: 'isCancellation',
          },
        },
        {
          target: 'rescheduleFlow',
          guard: {
            type: 'isReschedule',
          },
        },
        {
          target: 'rsvpFlow',
          guard: {
            type: 'isRsvp',
          },
        },
      ],
      entry: assign({
        language: ({context}) => {
          const eventDetails = context.eventDetails as IEvent;
          const users =
            context.owner === 'HOST' || context.owner === 'INTERVIEWER'
              ? eventDetails?.interviewers
              : eventDetails?.candidates;

          const currentUser = users?.find((user) => user.id === context.aid);
          return currentUser?.language || context.language;
        },
      }),
    },

    sequentialEventEditNotAllowed: {
      type: 'final',
    },

    sessionExpired: {
      type: 'final',
    },

    bookingSuccess: {
      always: [
        {
          target: 'sequentialBookingSuccess',
          guard: {
            type: 'isSequentialEvent',
          },
        },
        {
          target: 'generateAttendee',
        },
      ],
    },

    generateAttendee: {
      invoke: {
        input: ({context: {eventDetails}}) => ({
          eventId: (eventDetails as IEvent)?.id || '',
        }),
        src: 'generateAttendee',
        onDone: {
          target: 'attendeeGenerated',
          actions: assign({
            attendee: ({event}) => event.output,
            owner: 'CANDIDATE',
          }),
        },
      },
    },

    sequentialBookingSuccess: {
      type: 'final',
    },

    notifySuccess: {
      type: 'final',
    },

    cancellationFlow: {
      initial: 'init',
      states: {
        init: {
          always: [
            {
              target: 'cancellationPolicy',
              guard: {
                type: 'isCancellationNoticePass',
              },
            },
            {
              target: 'reasonForm',
              guard: {
                type: 'isAttendeeAvailable',
              },
            },
            {
              target: 'identificationFlow',
            },
          ],
        },
        cancellationPolicy: {
          type: 'final',
        },
        reasonForm: {
          initial: 'form',
          states: {
            form: {
              on: {
                'reasonForm.submit': {
                  target: 'submitting',
                },
              },
            },
            submitting: {
              entry: assign({
                reason: ({event}) => {
                  assertEvent(event, 'reasonForm.submit');
                  return event.reason;
                },
              }),
              invoke: {
                input: ({event, context}) => {
                  assertEvent(event, 'reasonForm.submit');
                  const eventDetails = context.eventDetails as IEvent;
                  if (eventDetails?.id) {
                    return {
                      eventId: eventDetails?.id,
                      reason: event.reason,
                      viewerTimezone:
                        eventDetails?.primary_guest_timezone || '',
                      attendee: context.attendee || '',
                      aid: context.aid || '',
                    };
                  }
                  throw new Error('eventId not found');
                },
                onDone: {
                  target: '#root.cancelSuccess',
                },
                onError: {
                  target: 'error',
                  actions: assign({
                    error: ({event}) => event.error,
                  }),
                },
                src: 'cancelEvent',
              },
            },
            error: {
              on: {
                'reasonForm.submit': {
                  target: 'submitting',
                },
              },
            },
          },
        },
        identificationFlow: {
          invoke: {
            id: 'rootNew.cancellationFlow.identificationFlow:invocation[0]',
            input: ({context}) => {
              const eventDetails = context.eventDetails as IEvent;
              if (eventDetails?.id) {
                return {
                  eventId: eventDetails?.id,
                  linkType: 'cancel',
                };
              }
              throw new Error('eventId not found');
            },
            src: 'identificationMachine',
            systemId: 'identificationMachine',
          },
        },
      },
    },

    rescheduleFlow: {
      initial: 'init',
      states: {
        limitNotReached: {
          always: [
            {
              target: 'reasonForm',
              guard: {
                type: 'isHostOrInterviewer',
              },
            },
            {
              target: 'bookingFlow',
              guard: {
                type: 'isCandidate',
              },
              actions: assign({
                isReschedule: true,
              }),
            },
            {
              target: 'identificationFlow',
            },
          ],
        },
        reasonForm: {
          initial: 'form',
          states: {
            form: {
              on: {
                'reasonForm.submit': {
                  target: 'submitting',
                  actions: assign({
                    reason: ({event}) => event.reason,
                  }),
                },
              },
            },

            submitting: {
              invoke: {
                id: 'rootNew.rescheduleFlow.rescheduleRequest.submitting:invocation[0]',
                input: ({event, context}) => {
                  assertEvent(event, 'reasonForm.submit');
                  const eventDetails = context.eventDetails as IEvent;
                  if (eventDetails?.id && context.aid) {
                    return {
                      eventId: eventDetails.id,
                      reason: event.reason || '',
                      aid: context.aid || '',
                    };
                  }
                  throw new Error('eventId or aid not found');
                },
                onDone: {
                  target: '#root.RequestRescheduleSuccess',
                  reenter: true,
                },
                onError: {
                  target: 'error',
                  actions: assign({error: ({event}) => event.error}),
                },
                src: 'sendRescheduleRequest',
              },
            },

            error: {
              on: {
                'reasonForm.submit': {
                  target: 'submitting',
                },
              },
            },
          },
        },
        bookingFlow: {
          on: {
            'language.update': {
              actions: [
                assign({
                  language: ({event}) => event.language,
                }),
                sendTo(
                  ({system}) => system.get('bookingFlow'),
                  ({event}) => ({
                    type: 'language.update',
                    language: event.language,
                  }),
                ),
              ],
            },
          },
          invoke: {
            id: 'bookingFlow',
            input: ({context}) => {
              const {timezone, attendee, language} = context;
              if (timezone) {
                return {
                  timezone,
                  eventDetails: context.eventDetails as IEvent,
                  attendee,
                  language,
                };
              }
              throw new Error('timezone not found');
            },
            onDone: [
              {
                target: '#root.bookingSuccess',
                actions: assign({
                  eventDetails: ({event}) => event.output.eventDetails,
                  reason: ({event}) => event.output.rescheduleReason,
                }),
                guard: {
                  type: 'isBookingSuccess',
                },
              },
              {
                target: '#root.notifySuccess',
              },
            ],
            src: 'bookingMachine',
            systemId: 'bookingMachine',
          },
        },
        identificationFlow: {
          invoke: {
            id: 'rootNew.rescheduleFlow.identificationFlow:invocation[0]',
            input: ({context}) => {
              const eventDetails = context.eventDetails as IEvent;
              if (eventDetails?.id) {
                return {
                  eventId: eventDetails?.id,
                  linkType: 'reschedule',
                };
              }
              throw new Error('eventId not found');
            },
            src: 'identificationMachine',
            systemId: 'identificationMachine',
          },
        },
        init: {
          always: [
            {
              target: 'rescheduleLimitReached',
              guard: 'isRescheduleLimitReached',
            },
            {
              target: 'limitNotReached',
              reenter: true,
            },
          ],
        },

        rescheduleLimitReached: {},
      },
    },

    rsvpFlow: {
      invoke: {
        id: 'rootNew.rsvpFlow:invocation[0]',
        input: ({context}) => {
          const {aid} = context;
          const eventDetails = context.eventDetails as IEvent;
          const rsvpStatus = getRsvpStatus(eventDetails, aid, context.owner);
          if (aid && eventDetails?.id) {
            return {
              eventId: eventDetails?.id,
              aid,
              rsvpStatus,
            };
          }
          throw new Error('aid and eventId not found');
        },
        src: 'rsvpMachine',
        systemId: 'rsvpMachine',
      },
    },

    bookingCancelForm: {
      on: {
        'bookingCancelForm.submit': {
          target: 'bookingCancelSubmitting',
          actions: assign({
            reason: ({event}) => event.reason,
          }),
        },
      },
    },

    bookingCancelSubmitting: {
      invoke: {
        input: ({event, context}) => {
          const eventDetails = context.eventDetails as IEvent;
          assertEvent(event, 'bookingCancelForm.submit');
          if (eventDetails?.id) {
            return {
              eventId: eventDetails?.id,
              reason: event.reason,
              viewerTimezone: eventDetails?.primary_guest_timezone || '',
              attendee: context.attendee || '',
              aid: eventDetails?.candidates?.[0]?.id || context.aid || '',
            };
          }
          throw new Error('eventId not found');
        },
        onDone: {
          target: 'cancelSuccess',
        },
        onError: {
          target: 'bookingCancelError',
          actions: assign({error: ({event}) => event.error}),
        },
        src: 'cancelEvent',
      },
    },

    cancelSuccess: {
      type: 'final',
    },

    bookingCancelError: {
      on: {
        'bookingCancelForm.submit': {
          target: 'bookingCancelSubmitting',
        },
      },
    },

    RequestRescheduleSuccess: {
      type: 'final',
    },

    attendeeGenerated: {
      on: {
        'reschedule.go': {
          target: 'rescheduleFlow',
        },
        'cancellation.go': {
          target: 'bookingCancelForm',
        },
      },
    },
  },
});

export type IRootMachineSnapshot = SnapshotFrom<typeof rootMachine>;
